import { IBluetoothPermissionLabelSetting, ICommonSettings, ILanguageModel } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { PureComponent, ReactNode, createRef } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigBluetoothSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingBluetoothSetting?: IBluetoothPermissionLabelSetting;
  bluetooth?: IBluetoothPermissionLabelSetting["values"];
}

export interface IConfigBluetoothSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingBluetoothId?: string;
  languages: ILanguageModel[];
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigBluetoothSettings extends PureComponent<
  IConfigBluetoothSettingsProps,
  IConfigBluetoothSettingsState
> {
  formRef = createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigBluetoothSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingBluetoothSetting: undefined,
      bluetooth: []
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigBluetoothSettingsProps) {
    if (prevProps.editingBluetoothId && !this.props.editingBluetoothId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingBluetoothId !== this.props.editingBluetoothId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (this.props.editingBluetoothId) {
      const setting = this.props.commonSettings.bluetoothPermissionLabelSettings?.find(
        (x) => x.id === this.props.editingBluetoothId
      );
      this.setState({ editingBluetoothSetting: setting }, () => {
        if (setting) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();
          fieldsValue.name = setting.name;
          this.formRef.current?.setFieldsValue(fieldsValue);
          this.setState({ bluetooth: setting.values });
        }
      });
    } else {
      const values: any[] = [];
      this.props.languages.forEach((language) => {
        values.push({
          languageName: language.shortName,
          cultureName: language.name,
          value: ""
        });
      });
      this.setState({ bluetooth: values });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const settings = this.props.commonSettings?.bluetoothPermissionLabelSettings;

    const isSettingAlreadyExist =
      settings?.some((x) => x.name === value && x.id !== this.props.editingBluetoothId) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Bluetooth Setting Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IBluetoothPermissionLabelSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      values: this.state.bluetooth?.map((bluetooth) => ({
        ...bluetooth,
        value: bluetooth.value?.trim()
      }))
    };
    if (this.props.editingBluetoothId) {
      obj.id = this.props.editingBluetoothId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "bluetoothPermissionLabelSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "bluetoothPermissionLabelSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingBluetoothId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    const parentDataKey = this.props.formInputName[0] === "androidConfig" ? "iosConfig" : "androidConfig";
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingBluetoothId || "",
      configKey: "bluetoothPermissionLabelSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Bluetooth setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      shouldControlOtherSections: {
        listOfControl: [[parentDataKey, this.props.formInputName[1]]],
        onCannotDelete: () => {
          KMainFunctions.displayErrorNotification(`
          This Bluetooth setting is
          used on ${
            this.props.formInputName[0] === "androidConfig" ? "IOS" : "Android"
          } section. Please discard this setting
          on other config section before delete.
        `);
        }
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  setValue = (e, lang) => {
    const bluetooth = _.cloneDeep(this.state.bluetooth);
    bluetooth?.forEach((value) => {
      if (value.cultureName === lang.cultureName) {
        value.value = e.target.value;
      }
    });
    this.setState({ bluetooth });
  };

  getSectionDescription = (): ReactNode => {
    return this.props.getSectionDescription("bluetoothpopup_bluetoothpopup_description");
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Bluetooth setting"
        secondHeader="Bluetooth setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingBluetoothId ? this.deleteSetting : undefined}
            deleteTestId="bluetoothConfigurationModalDeleteButton"
            submitTestId={`bluetoothConfigurationModal${this.props.editingBluetoothId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingBluetoothId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configBluetoothForm"
          />
        }
      >
        <Form
          id="configBluetoothForm"
          name="bluetoothSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
        >
          <ConfigModalBody description={this.getSectionDescription()}>
            <ConfigField label="Name" required tooltipContentKey="bluetoothpopup_bluetoothpopup_name">
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Bluetooth Setting Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Bluetooth Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="bluetoothSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            {this.state.bluetooth?.map((value) => {
              return (
                <ConfigField
                  key={value.cultureName}
                  label={value.languageName}
                  tooltipContentKey="bluetoothpopup_bluetoothpopup_english"
                >
                  <div style={{ width: 380, marginBottom: 24 }}>
                    <KuikaInput
                      size="md"
                      data-testid="bluetoothSettingEnglishInput"
                      value={value.value}
                      onChange={(e) => this.setValue(e, value)}
                    />
                  </div>
                </ConfigField>
              );
            })}
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
